import {
  CouponPayment,
  MoodysRating,
  OrderDirection,
  OrderStatus,
  OrderTicketId,
  SpRating,
  TimeInForce,
} from "@goodfill/domain-properties";
import { CorporateBondId, IssuerId } from "@goodfill/domain-properties";
import { PresentedOrderTicket } from "./PresentedOrderTicket";
import {
  maturityDateFilter,
  MATURITY_CRITERIA_KEY,
} from "../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import { FilterFunction } from "../../../../filters/filters";
import { DateRange } from "../../../../filters/DateTimeRangeFilter";
import { isinFilter, ISIN_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  issuerIdFilter,
  ISSUER_ID_CRITERIA_KEY,
} from "../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import {
  COUPON_PAYMENT_CRITERIA_KEY,
  couponPaymentFilter,
} from "../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";
import { DecimalRange } from "../../../../filters/DecimalRangeFilter";
import {
  ALREADY_FILLED_AMOUNT_CRITERIA_KEY,
  alreadyFilledAmountFilter,
} from "../../../DataGridFilteredColumns/order/AlreadyFilledAmountColumnDefinition";
import {
  MOODYS_RATING_CRITERIA_KEY,
  moodysRatingFilter,
} from "../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  PAYMENT_RANK_CRITERIA_KEY,
  paymentRankFilter,
} from "../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import {
  SP_RATING_CRITERIA_KEY,
  spRatingFilter,
} from "../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import { TICKER_CRITERIA_KEY, tickersFilter } from "../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import {
  CREATED_AT_DATE_CRITERIA_KEY,
  createdDateFilter,
} from "../../../DataGridFilteredColumns/order/CreatedAtDateColumnDefinition";
import {
  EXPIRES_AT_DATE_CRITERIA_KEY,
  expiresAtDateFilter,
} from "../../../DataGridFilteredColumns/order/ExpiresDateColumnDefinition";
import {
  FACE_AMOUNT_CRITERIA_KEY,
  faceAmountFilter,
} from "../../../DataGridFilteredColumns/order/FaceAmountColumnDefinition";
import {
  ORDER_STATUS_CRITERIA_KEY,
  orderStatusFilter,
} from "../../../DataGridFilteredColumns/order/OrderStatusColumnDefinition";
import {
  ORDER_TICKET_ID_CRITERIA_KEY,
  orderTicketIdFilter,
} from "../../../DataGridFilteredColumns/order/OrderTicketIdColumnDefinition";
import {
  TIME_IN_FORCE_CRITERIA_KEY,
  timeInForceFilter,
} from "../../../DataGridFilteredColumns/order/TimeInForceColumnDefinition";
import {
  ORDER_BALANCE_AMOUNT_CRITERIA_KEY,
  orderBalanceAmountFilter,
} from "../../../DataGridFilteredColumns/order/OrderBalanceAmountColumnDefinition";
import {
  LOADED_BOND_PRICE_CRITERIA_KEY,
  postFeePriceFilter,
} from "../../../DataGridFilteredColumns/order/PostFeePriceColumnDefinition";
import { Range } from "../../../DataGridFilteredColumns/generic/RangeColumnDefinition";

export interface OrderTicketFilter {
  [ORDER_STATUS_CRITERIA_KEY]: OrderStatus[];
  direction: OrderDirection | null;
  [TIME_IN_FORCE_CRITERIA_KEY]: TimeInForce[];
  [ISIN_CRITERIA_KEY]: CorporateBondId[];
  [ISSUER_ID_CRITERIA_KEY]: IssuerId[];
  [TICKER_CRITERIA_KEY]: string[];
  [FACE_AMOUNT_CRITERIA_KEY]: DecimalRange;
  minimumFillSize: string;
  [ORDER_TICKET_ID_CRITERIA_KEY]: OrderTicketId[];
  [MOODYS_RATING_CRITERIA_KEY]: Range<MoodysRating>;
  [SP_RATING_CRITERIA_KEY]: Range<SpRating>;
  [MATURITY_CRITERIA_KEY]: DateRange;
  [CREATED_AT_DATE_CRITERIA_KEY]: DateRange;
  [EXPIRES_AT_DATE_CRITERIA_KEY]: DateRange;
  [PAYMENT_RANK_CRITERIA_KEY]: string[];
  [COUPON_PAYMENT_CRITERIA_KEY]: CouponPayment[];
  [ALREADY_FILLED_AMOUNT_CRITERIA_KEY]: DecimalRange;
  [ORDER_BALANCE_AMOUNT_CRITERIA_KEY]: DecimalRange;
  [LOADED_BOND_PRICE_CRITERIA_KEY]: DecimalRange;
}

export const defaultOrderFilterConstraints: OrderTicketFilter = {
  [ORDER_STATUS_CRITERIA_KEY]: [],
  direction: null,
  [TIME_IN_FORCE_CRITERIA_KEY]: [],
  [ISIN_CRITERIA_KEY]: [],
  [ISSUER_ID_CRITERIA_KEY]: [],
  [FACE_AMOUNT_CRITERIA_KEY]: [null, null],
  minimumFillSize: "",
  [TICKER_CRITERIA_KEY]: [],
  [ORDER_TICKET_ID_CRITERIA_KEY]: [],
  [MOODYS_RATING_CRITERIA_KEY]: [null, null],
  [SP_RATING_CRITERIA_KEY]: [null, null],
  [MATURITY_CRITERIA_KEY]: [null, null],
  [CREATED_AT_DATE_CRITERIA_KEY]: [null, null],
  [EXPIRES_AT_DATE_CRITERIA_KEY]: [null, null],
  [PAYMENT_RANK_CRITERIA_KEY]: [],
  [COUPON_PAYMENT_CRITERIA_KEY]: [],
  [ALREADY_FILLED_AMOUNT_CRITERIA_KEY]: [null, null],
  [ORDER_BALANCE_AMOUNT_CRITERIA_KEY]: [null, null],
  [LOADED_BOND_PRICE_CRITERIA_KEY]: [null, null],
};

export const orderFilters: FilterFunction<PresentedOrderTicket, OrderTicketFilter>[] = [
  (item, criteria) => !criteria.direction || criteria.direction === item.direction,

  tickersFilter("ticker"),
  isinFilter("bondId"),
  issuerIdFilter("issuerId"),
  timeInForceFilter("timeInForce"),
  orderStatusFilter("status"),
  orderTicketIdFilter("id"),
  faceAmountFilter("faceAmount"),
  spRatingFilter("bondRatingSp"),
  moodysRatingFilter("bondRatingMoodys"),
  createdDateFilter("createdAt"),
  expiresAtDateFilter("expiresAt"),
  maturityDateFilter("bondMaturityDate"),
  paymentRankFilter("bondPaymentRank"),
  couponPaymentFilter("bondCouponPayment"),
  alreadyFilledAmountFilter("alreadyFilled"),
  orderBalanceAmountFilter("balanceAmount"),
  postFeePriceFilter("loadedBondPrice"),
];
